import { Injectable, computed, inject, signal, untracked } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, defer, of, throwError } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { RequestsApiService } from '../api/services/requests-api.service';
import { RequestBoardConfigService, isRequestBoardDisabledError } from './request-board-config.service';
import { RequestTokensService } from './request-tokens.service';
import { forgetSensitiveValue } from './sensitive-url-params';
import {
  ApiErrorBodyDto,
  ApiValidationErrorsDto,
  BoardQuery,
  CloseReason,
  CreateRequestDto,
  CreateRequestResponseDto,
  MatchesQuery,
  MatchesResultDto,
  PaginationMetaDto,
  ProposalPublicDto,
  ProposalReplyResponseDto,
  ReportRequestDto,
  ReportRequestResponseDto,
  RequestBoardError,
  RequestBoardPublicConfigDto,
  RequestBoardRateLimitScope,
  RequestCardDto,
  RequestDetailDto,
  RequestStatsDto,
  ResendVerificationResponseDto,
  UpdateRequestDto,
  isHeaderSafeToken,
} from '../api/models';

const RATE_LIMITED = 'REQUEST_BOARD.RATE_LIMITED';
const NOT_FOUND = 'REQUEST_BOARD.NOT_FOUND';
const INVALID_TOKEN = 'REQUEST_BOARD.INVALID_TOKEN';

/**
 * DTO field → `REQUEST_BOARD.*` code. The backend global `ValidationException`
 * wraps class-validator failures as `VALIDATION.MULTIPLE_ERRORS` + per-field
 * `VALIDATION.*` codes (the `REQUEST_BOARD.*` messages set on the DTOs are
 * discarded there), so the field name is the only hint of what went wrong.
 * Ordered by priority — the first matching field wins.
 */
const VALIDATION_FIELD_CODES: readonly (readonly [RegExp, string])[] = [
  [/^formToken$/, 'REQUEST_BOARD.FORM_TOKEN_INVALID'],
  [/^termsAccepted$/, 'REQUEST_BOARD.TERMS_REQUIRED'],
  [/^offersConsent$/, 'REQUEST_BOARD.CONSENT_REQUIRED'],
  [/^(checkIn|checkOut|windowFrom|windowTo|nights|dateMode|flexDays)$/, 'REQUEST_BOARD.INVALID_DATES'],
  [/^(guestEmail|reporterEmail)$/, 'VALIDATION.INVALID_EMAIL'],
];

/**
 * Derives one `errorCode` from the per-field validation codes (top-level field
 * name only — `towns.0.name` → `towns`). Unknown fields → INVALID_CRITERIA.
 */
export function validationErrorCode(validationErrors: ApiValidationErrorsDto): string {
  const fields = Object.keys(validationErrors).map((f) => f.split('.')[0]);
  if (fields.length === 0) return 'REQUEST_BOARD.INVALID_CRITERIA';
  for (const [pattern, code] of VALIDATION_FIELD_CODES) {
    if (fields.some((f) => pattern.test(f))) return code;
  }
  return 'REQUEST_BOARD.INVALID_CRITERIA';
}

/** Options of `mapRequestBoardError`. */
export interface MapRequestBoardErrorOptions {
  /**
   * The call addresses ONE request by id (`/:id`, `/:id/matches`,
   * `/:id/proposals`, author actions, verify, report). There a generic
   * `COMMON.NOT_FOUND` (unknown route) or `COMMON.BAD_REQUEST` (`ParseUUIDPipe`
   * — a malformed id from a link) means „no such request” →
   * `REQUEST_BOARD.NOT_FOUND`.
   */
  byId?: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Already mapped (e.g. the client-side „missing token” error re-mapped by a caller). */
function isRequestBoardError(value: unknown): value is RequestBoardError {
  return isRecord(value) && typeof value['errorCode'] === 'string' && typeof value['status'] === 'number';
}

/** Generic backend codes renamed for the UI. */
const RENAMED_CODES: ReadonlyMap<string, string> = new Map([['COMMON.PAYLOAD_TOO_LARGE', 'HTTP_413']]);
/** Generic codes meaning „no such request” on a call addressing one request by id. */
const BY_ID_NOT_FOUND_CODES: ReadonlySet<string> = new Set(['COMMON.NOT_FOUND', 'COMMON.BAD_REQUEST']);
/** Code of a body without any (proxy error page), by HTTP status; else `HTTP_<status>`. */
const STATUS_FALLBACK_CODES: ReadonlyMap<number, string> = new Map([
  [404, NOT_FOUND],
  [409, 'REQUEST_BOARD.DUPLICATE'],
]);

/** The body's `errorCode` — a DTO validation failure resolved to the code of the failing field. */
function readErrorCode(body: ApiErrorBodyDto, validationErrors?: ApiValidationErrorsDto): string | null {
  const code = typeof body.errorCode === 'string' && body.errorCode ? body.errorCode : null;
  return code === 'VALIDATION.MULTIPLE_ERRORS' && validationErrors ? validationErrorCode(validationErrors) : code;
}

/** The route throttler (`COMMON.RATE_LIMIT_EXCEEDED`) or a 429 without a `REQUEST_BOARD.*` code (proxy). */
function isThrottled(code: string | null, status: number): boolean {
  return code === 'COMMON.RATE_LIMIT_EXCEEDED' || (status === 429 && !(code ?? '').startsWith('REQUEST_BOARD.'));
}

/** Throttler bodies carry no scope — `'throttle'` tells the UI to say „slow down”. */
function withThrottleScope(data?: Record<string, unknown>): Record<string, unknown> {
  return typeof data?.['scope'] === 'string' ? data : { ...data, scope: 'throttle' };
}

/** Normalises a (non-throttle) code: renames, by-id „not found”, the status fallback. */
function normaliseCode(code: string | null, status: number, byId: boolean): string {
  if (code === null) return STATUS_FALLBACK_CODES.get(status) ?? `HTTP_${status}`;
  if (byId && BY_ID_NOT_FOUND_CODES.has(code)) return NOT_FOUND;
  return RENAMED_CODES.get(code) ?? code;
}

function errorMessage(body: ApiErrorBodyDto, err: HttpErrorResponse): string {
  const raw = body.message;
  return Array.isArray(raw) ? raw.join(', ') : raw || err.message || 'Request failed';
}

function mapHttpError(err: HttpErrorResponse, byId: boolean): RequestBoardError {
  const body: ApiErrorBodyDto = isRecord(err.error) ? (err.error as ApiErrorBodyDto) : {};
  const validationErrors = isRecord(body.validationErrors) ? body.validationErrors : undefined;
  const code = readErrorCode(body, validationErrors);
  const throttled = isThrottled(code, err.status);
  const rawData = isRecord(body.data) ? body.data : undefined;
  const data = throttled ? withThrottleScope(rawData) : rawData;
  const mapped: RequestBoardError = {
    status: err.status,
    errorCode: throttled ? RATE_LIMITED : normaliseCode(code, err.status, byId),
    message: errorMessage(body, err),
  };
  if (data) mapped.data = data;
  if (validationErrors) mapped.validationErrors = validationErrors;
  return mapped;
}

/**
 * Maps an HTTP error to a `RequestBoardError` with a stable `errorCode` the UI
 * can switch on. The backend always sends an `errorCode`; the generic ones are
 * normalised here:
 * - DTO validation (`VALIDATION.MULTIPLE_ERRORS` + `validationErrors`) → the
 *   `REQUEST_BOARD.*` code of the failing field (e.g. `FORM_TOKEN_INVALID`,
 *   which makes the UI refresh the form token);
 * - any 429 / `COMMON.RATE_LIMIT_EXCEEDED` (route throttler) →
 *   `REQUEST_BOARD.RATE_LIMITED`; the backend `data.scope` is kept, the
 *   throttler (no scope) gets `data.scope = 'throttle'`;
 * - `COMMON.PAYLOAD_TOO_LARGE` → `HTTP_413`;
 * - `options.byId`: `COMMON.NOT_FOUND` / `COMMON.BAD_REQUEST` →
 *   `REQUEST_BOARD.NOT_FOUND`;
 * - no code at all (proxy error page) → 404 NOT_FOUND, 409 DUPLICATE, else
 *   `HTTP_<status>`.
 * `REQUEST_BOARD.DISABLED` (503) and the other `REQUEST_BOARD.*` codes pass through.
 */
export function mapRequestBoardError(
  err: unknown,
  options: MapRequestBoardErrorOptions = {},
): RequestBoardError {
  if (err instanceof HttpErrorResponse) return mapHttpError(err, options.byId === true);
  if (isRequestBoardError(err)) return err;
  return { status: 0, errorCode: 'HTTP_0', message: err instanceof Error ? err.message : 'Unknown error' };
}

/**
 * Where an error is shown — picks the wording of the generic fallback and of
 * the codes whose meaning depends on the situation:
 * - `create` — publishing the request form (default);
 * - `edit` — saving the edited request (author);
 * - `extract` — „Wklej post z FB” box;
 * - `action` — other author actions: extend, consent toggle, resend, close,
 *   delete, reply to a proposal (never „could not send the request”);
 * - `report` — the DSA report dialog;
 * - `load` — reading the board / a request.
 */
export type RequestBoardErrorContext = 'create' | 'edit' | 'extract' | 'action' | 'report' | 'load';

const GENERIC_ERROR_KEYS: Readonly<Record<RequestBoardErrorContext, string>> = {
  create: 'requestBoard.form.errors.generic',
  edit: 'requestBoard.errors.generic.edit',
  extract: 'requestBoard.form.paste.errors.generic',
  action: 'requestBoard.errors.generic.action',
  report: 'requestBoard.detail.report.error',
  load: 'requestBoard.errors.generic.load',
};

/** Contexts with a form the guest filled in — validation wording makes sense there. */
const FORM_CONTEXTS: ReadonlySet<RequestBoardErrorContext> = new Set(['create', 'edit', 'report']);
/** Contexts where „too long” (413) points at the guest's text. */
const TOO_LARGE_CONTEXTS: ReadonlySet<RequestBoardErrorContext> = new Set(['create', 'edit']);

/** Rate-limit codes — raw throttler ones too (callers may pass codes that skipped `mapRequestBoardError`). */
const RATE_LIMIT_CODES: ReadonlySet<string> = new Set([RATE_LIMITED, 'COMMON.RATE_LIMIT_EXCEEDED', 'HTTP_429']);
const TOO_LARGE_CODES: ReadonlySet<string> = new Set(['HTTP_413', 'COMMON.PAYLOAD_TOO_LARGE']);
const BAD_REQUEST_CODES: ReadonlySet<string> = new Set(['HTTP_400', 'COMMON.BAD_REQUEST']);

const DAILY_LIMIT_KEY = 'requestBoard.errors.rateLimited.daily';
const THROTTLE_KEY = 'requestBoard.errors.rateLimited.throttle';
const PASTE_INVALID_KEY = 'requestBoard.form.paste.errors.invalid';

/**
 * i18n key of each `RATE_LIMITED` scope — what to do depends on which limit
 * was hit. Typed by the scope union: a new backend scope added to
 * `RequestBoardRateLimitScope` does not compile until it gets its wording.
 */
const RATE_LIMIT_SCOPE_KEYS: Readonly<Record<RequestBoardRateLimitScope, string>> = {
  active_per_email: 'requestBoard.errors.rateLimited.activePerEmail',
  email_daily: DAILY_LIMIT_KEY,
  ip_daily: DAILY_LIMIT_KEY,
  report_ip_daily: DAILY_LIMIT_KEY,
  report_email_daily: DAILY_LIMIT_KEY,
  extract_ip_daily: 'requestBoard.form.paste.errors.rateLimited',
  throttle: THROTTLE_KEY,
};

function isRateLimitScope(scope: string | null): scope is RequestBoardRateLimitScope {
  return scope !== null && Object.prototype.hasOwnProperty.call(RATE_LIMIT_SCOPE_KEYS, scope);
}

/** What `requestBoardErrorKey` reads of an error. */
export type RequestBoardErrorKeySource = Pick<RequestBoardError, 'errorCode'> & Partial<Pick<RequestBoardError, 'data'>>;

/** `data.scope` of a rate-limit error; raw throttler codes mean „slow down”, a bare RATE_LIMITED → null. */
function rateLimitScopeOf(err: RequestBoardErrorKeySource): string | null {
  const scope = err.data?.['scope'];
  if (typeof scope === 'string') return scope;
  return err.errorCode === RATE_LIMITED ? null : 'throttle';
}

/** i18n key of a RATE_LIMITED error (see `RATE_LIMIT_SCOPE_KEYS`). */
function rateLimitedKey(scope: string | null, context: RequestBoardErrorContext): string {
  if (isRateLimitScope(scope)) return RATE_LIMIT_SCOPE_KEYS[scope];
  // Unknown scope: the historic wording fits publishing only.
  return context === 'create' ? `errors.${RATE_LIMITED}` : THROTTLE_KEY;
}

/** „Wklej post” — the paste box has its own wording (manual fill-in is the way out). */
const EXTRACT_ERROR_KEYS: ReadonlyMap<string, string> = new Map([
  ['REQUEST_BOARD.EXTRACT_DISABLED', 'errors.REQUEST_BOARD.EXTRACT_DISABLED'],
  ['REQUEST_BOARD.DISABLED', 'errors.REQUEST_BOARD.DISABLED'],
  ['REQUEST_BOARD.FORM_TOKEN_INVALID', 'requestBoard.form.errors.formTokenRefreshed'],
  ['REQUEST_BOARD.INVALID_CRITERIA', PASTE_INVALID_KEY],
  ['HTTP_400', PASTE_INVALID_KEY],
  ['COMMON.BAD_REQUEST', PASTE_INVALID_KEY],
]);

function extractErrorKeyFor(code: string, generic: string): string {
  return EXTRACT_ERROR_KEYS.get(code) ?? (code.startsWith('VALIDATION.') ? PASTE_INVALID_KEY : generic);
}

/** Validation wording of a form context, or null (not a validation failure). */
function formErrorKey(code: string): string | null {
  if (code === 'VALIDATION.INVALID_EMAIL') return 'requestBoard.form.validation.emailInvalid';
  // Any other class-validator failure (single code or MULTIPLE_ERRORS without fields).
  if (code.startsWith('VALIDATION.') || BAD_REQUEST_CODES.has(code)) return 'errors.REQUEST_BOARD.INVALID_CRITERIA';
  return null;
}

/**
 * Maps a `RequestBoardError` (as produced by `mapRequestBoardError`) to the
 * i18n key of the message shown to the user. `context` tells where it is shown
 * (see `RequestBoardErrorContext`); `REQUEST_BOARD.RATE_LIMITED` is worded by
 * `data.scope` (too many active requests / daily limit / slow down).
 */
export function requestBoardErrorKey(
  err: RequestBoardErrorKeySource,
  context: RequestBoardErrorContext = 'create',
): string {
  const code = err.errorCode;
  const generic = GENERIC_ERROR_KEYS[context] ?? GENERIC_ERROR_KEYS.create;
  if (RATE_LIMIT_CODES.has(code)) return rateLimitedKey(rateLimitScopeOf(err), context);
  if (context === 'extract') return extractErrorKeyFor(code, generic);
  if (code.startsWith('REQUEST_BOARD.')) return `errors.${code}`;
  const formKey = FORM_CONTEXTS.has(context) ? formErrorKey(code) : null;
  if (formKey) return formKey;
  if (TOO_LARGE_CODES.has(code) && TOO_LARGE_CONTEXTS.has(context)) return 'requestBoard.form.errors.tooLong';
  return generic;
}

/**
 * Author-action errors after which the store's copy of the request is stale
 * (status changed in parallel, request / proposal gone, token no longer valid):
 * the current request — and its proposals — are re-read in the background.
 */
const STALE_AFTER_ACTION_CODES: ReadonlySet<string> = new Set([
  'REQUEST_BOARD.INVALID_STATE',
  NOT_FOUND,
  INVALID_TOKEN,
  'REQUEST_BOARD.PROPOSAL_NOT_FOUND',
]);

const BY_ID: MapRequestBoardErrorOptions = { byId: true };

/** Appends `next` to `prev` skipping ids already present (offset pagination can repeat items). */
function appendUniqueById<T extends { id: string | number }>(prev: readonly T[], next: readonly T[]): T[] {
  const seen = new Set(prev.map((item) => item.id));
  const out = [...prev];
  for (const item of next) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(item);
  }
  return out;
}

/** More matches to fetch (page arithmetic too — de-duplicated items may never reach `total`). */
function hasMoreMatches(m: MatchesResultDto | null): boolean {
  if (!m || m.items.length >= m.total) return false;
  return !(m.limit > 0 && m.page * m.limit >= m.total);
}

/** Author action context: request id + the manage token the call was made with. */
interface AuthorCall {
  id: string;
  token: string;
}

/**
 * The backend gave its final word on a credential: any 4xx but a timeout (408)
 * or a rate limit (429). Network errors (0) and 5xx are transient.
 */
function isDefinitiveRejection(error: RequestBoardError): boolean {
  return error.status >= 400 && error.status < 500 && error.status !== 408 && error.status !== 429;
}

/**
 * Signal store of the public request board. State only — HTTP goes through
 * `RequestsApiService`, tokens through `RequestTokensService`, config through
 * `RequestBoardConfigService`.
 *
 * Read operations (`load*`) update signals and swallow errors into `*Error`
 * signals. Mutations (`create`, `verify`, `update`, `close`, `extend`,
 * `remove`, `reply`) update state in `tap()` and RETURN an observable whose
 * error is already mapped to `RequestBoardError` — callers subscribe and
 * handle navigation / messages.
 *
 * Races: requests are not cancelled, their responses are checked instead. The
 * board list has a generation counter (a newer `loadBoard` makes the older
 * list / „load more” responses void). The request-scoped state (current,
 * matches, proposals) belongs to ONE focused id — responses for another id
 * (A → B navigation) and superseded responses of the same id are dropped, and
 * a stale response never resets the loading flag of a newer request. `load*`
 * never takes the focus from another request (a late call with an id captured
 * before the navigation is a no-op): switch with `clearCurrent()` first.
 *
 * Tokens: every author response refreshes the local validity of the manage
 * token (`expiresAt + 30 d` moves with extend / date edits); a token the
 * backend no longer accepts (GET → `isAuthor: false`, action → 403) is removed.
 * A credential taken from an e-mail link is released from the URL holder
 * (`sensitive-url-params`, kept per tab for a reload) once the backend gave its
 * final answer for it.
 * Any `REQUEST_BOARD.DISABLED` answer switches `RequestBoardConfigService.enabled()` off.
 */
@Injectable({ providedIn: 'root' })
export class RequestBoardStore {
  private readonly api = inject(RequestsApiService);
  private readonly configService = inject(RequestBoardConfigService);
  private readonly tokens = inject(RequestTokensService);

  // ── Config ──
  readonly config = this.configService.config;
  readonly configLoading = this.configService.loading;

  // ── Stats ──
  private readonly _stats = signal<RequestStatsDto | null>(null);
  private readonly _statsLoading = signal(false);
  private statsLoadedAt: number | null = null;

  // ── Board list ──
  private readonly _board = signal<RequestCardDto[]>([]);
  private readonly _boardMeta = signal<PaginationMetaDto | null>(null);
  private readonly _boardQuery = signal<BoardQuery>({});
  private readonly _boardLoading = signal(false);
  private readonly _boardLoadingMore = signal(false);
  private readonly _boardError = signal<RequestBoardError | null>(null);
  /** Error of the last `loadBoardNextPage` — kept apart so the loaded list stays visible. */
  private readonly _boardLoadMoreError = signal<RequestBoardError | null>(null);
  /** Bumped by `loadBoard` / `clearBoard`: responses of an older generation are dropped. */
  private boardGeneration = 0;

  // ── Current request ──
  private readonly _current = signal<RequestDetailDto | null>(null);
  private readonly _currentLoading = signal(false);
  private readonly _currentError = signal<RequestBoardError | null>(null);

  // ── Matches ──
  private readonly _matches = signal<MatchesResultDto | null>(null);
  private readonly _matchesLoading = signal(false);
  private readonly _matchesLoadingMore = signal(false);
  private readonly _matchesError = signal<RequestBoardError | null>(null);

  // ── Proposals ──
  private readonly _proposals = signal<ProposalPublicDto[]>([]);
  private readonly _proposalsLoading = signal(false);
  private readonly _proposalsError = signal<RequestBoardError | null>(null);

  // ── Request scope (race guards) ──
  /** Id the current / matches / proposals state belongs to (null = none). */
  private focusId: string | null = null;
  private currentGeneration = 0;
  private matchesGeneration = 0;
  private proposalsGeneration = 0;
  /** `loadProposals` ran for the focused request → refreshed after stale-state errors. */
  private proposalsRequested = false;

  // ── Mutations ──
  /** Number of in-flight mutations (overlapping actions do not clear each other). */
  private readonly _pendingActions = signal(0);
  private readonly _actionError = signal<RequestBoardError | null>(null);

  readonly stats = this._stats.asReadonly();
  readonly statsLoading = this._statsLoading.asReadonly();
  readonly activeCount = computed(() => this._stats()?.active ?? 0);

  readonly board = this._board.asReadonly();
  readonly boardMeta = this._boardMeta.asReadonly();
  readonly boardQuery = this._boardQuery.asReadonly();
  readonly boardLoading = this._boardLoading.asReadonly();
  readonly boardLoadingMore = this._boardLoadingMore.asReadonly();
  readonly boardError = this._boardError.asReadonly();
  readonly boardLoadMoreError = this._boardLoadMoreError.asReadonly();
  readonly boardTotal = computed(() => this._boardMeta()?.total ?? 0);
  readonly boardHasNextPage = computed(() => this._boardMeta()?.hasNextPage ?? false);
  readonly boardIsEmpty = computed(() => this._board().length === 0 && !this._boardLoading());

  readonly current = this._current.asReadonly();
  readonly currentLoading = this._currentLoading.asReadonly();
  readonly currentError = this._currentError.asReadonly();
  /** Manage token of the current request known to this browser (or null). */
  readonly currentToken = computed(() => {
    const id = this._current()?.id;
    return id ? this.tokens.get(id) : null;
  });
  readonly isAuthor = computed(() => this._current()?.isAuthor ?? false);

  readonly matches = this._matches.asReadonly();
  readonly matchesLoading = this._matchesLoading.asReadonly();
  readonly matchesLoadingMore = this._matchesLoadingMore.asReadonly();
  readonly matchesError = this._matchesError.asReadonly();
  readonly matchItems = computed(() => this._matches()?.items ?? []);
  readonly matchesTotal = computed(() => this._matches()?.total ?? 0);
  readonly matchesHasMore = computed(() => hasMoreMatches(this._matches()));

  readonly proposals = this._proposals.asReadonly();
  readonly proposalsLoading = this._proposalsLoading.asReadonly();
  readonly proposalsError = this._proposalsError.asReadonly();
  readonly proposalsCount = computed(() => this._proposals().length);

  readonly submitting = computed(() => this._pendingActions() > 0);
  readonly actionError = this._actionError.asReadonly();

  // ────────────────────────────────────────────────────────────
  // Config / stats
  // ────────────────────────────────────────────────────────────

  loadConfig(force = false): Observable<RequestBoardPublicConfigDto> {
    return this.configService.load(force);
  }

  /** GET /stats (backend caches 60 s; we avoid re-fetching within 60 s too). */
  loadStats(force = false): void {
    const now = Date.now();
    if (!force && this.statsLoadedAt !== null && now - this.statsLoadedAt < 60_000) return;
    if (this._statsLoading()) return;
    this._statsLoading.set(true);
    this.api
      .stats()
      .pipe(
        tap((stats) => {
          this._stats.set(stats);
          this.statsLoadedAt = Date.now();
        }),
        catchError((err: unknown) => {
          this.fail(err);
          return of(null);
        }),
        finalize(() => this._statsLoading.set(false)),
      )
      .subscribe();
  }

  // ────────────────────────────────────────────────────────────
  // Board list
  // ────────────────────────────────────────────────────────────

  loadBoard(query: BoardQuery = {}): void {
    const normalized: BoardQuery = { ...query, page: query.page ?? 1 };
    const generation = ++this.boardGeneration;
    this._boardQuery.set(normalized);
    this._boardLoading.set(true);
    // A „load more” still in flight belongs to the previous list (its response is dropped).
    this._boardLoadingMore.set(false);
    this._boardError.set(null);
    this._boardLoadMoreError.set(null);
    this.api
      .list(normalized)
      .pipe(
        tap((page) => {
          if (generation !== this.boardGeneration) return;
          this._board.set(appendUniqueById([], page.data));
          this._boardMeta.set(page.meta);
        }),
        catchError((err: unknown) => {
          const error = this.fail(err);
          if (generation === this.boardGeneration) {
            this._boardError.set(error);
            this._board.set([]);
            this._boardMeta.set(null);
          }
          return of(null);
        }),
        finalize(() => {
          if (generation === this.boardGeneration) this._boardLoading.set(false);
        }),
      )
      .subscribe();
  }

  loadBoardNextPage(): void {
    const meta = this._boardMeta();
    if (!meta?.hasNextPage || this._boardLoading() || this._boardLoadingMore()) return;
    const generation = this.boardGeneration;
    const nextQuery: BoardQuery = { ...this._boardQuery(), page: meta.page + 1 };
    // Recorded before the request — a late response can never write old filters back.
    this._boardQuery.set(nextQuery);
    this._boardLoadingMore.set(true);
    this._boardLoadMoreError.set(null);
    this.api
      .list(nextQuery)
      .pipe(
        tap((page) => {
          if (generation !== this.boardGeneration) return;
          // Offset pagination over `createdAt DESC`: a request published meanwhile
          // shifts the pages by one — skip the card already shown.
          this._board.update((prev) => appendUniqueById(prev, page.data));
          this._boardMeta.set(page.meta);
        }),
        catchError((err: unknown) => {
          const error = this.fail(err);
          // Not `boardError`: that would swap the whole list for the error block.
          if (generation === this.boardGeneration) this._boardLoadMoreError.set(error);
          return of(null);
        }),
        finalize(() => {
          if (generation === this.boardGeneration) this._boardLoadingMore.set(false);
        }),
      )
      .subscribe();
  }

  clearBoard(): void {
    this.boardGeneration++;
    this._board.set([]);
    this._boardMeta.set(null);
    this._boardError.set(null);
    this._boardLoadMoreError.set(null);
    this._boardLoading.set(false);
    this._boardLoadingMore.set(false);
  }

  // ────────────────────────────────────────────────────────────
  // Current request / matches / proposals
  // ────────────────────────────────────────────────────────────

  /**
   * Loads the detail of the focused request — or focuses the store on `id`
   * when nothing is focused (`clearCurrent()` first to move from another
   * request; a call for another id is a no-op, see `claimFocus`). The token
   * defaults to the one stored for this id (also when the given one cannot be
   * sent — see `usableToken`); the author's token validity is refreshed from
   * the response, a token the backend no longer accepts is forgotten.
   */
  loadRequest(id: string, token?: string | null): void {
    if (!this.claimFocus(id)) return;
    this.fetchCurrent(id, this.usableToken(id, token), false);
  }

  setCurrent(request: RequestDetailDto | null): void {
    if (request) {
      this.showRequest(request);
      return;
    }
    this.currentGeneration++;
    this._current.set(null);
    this._currentError.set(null);
    this._currentLoading.set(false);
  }

  /** Drops the request-scoped state (and voids the responses still in flight). */
  clearCurrent(): void {
    this.resetRequestScope(null);
  }

  /**
   * GET /:id/matches — the author's manage token (stored, or `query.t`) is
   * sent in the header: a draft is visible to its author only (404 otherwise).
   * No-op for a request other than the focused one (`claimFocus`).
   */
  loadMatches(id: string, query: MatchesQuery = { relax: 'auto' }): void {
    if (!this.claimFocus(id)) return;
    const generation = ++this.matchesGeneration;
    this._matchesLoading.set(true);
    // A next page still in flight belongs to the previous result (dropped).
    this._matchesLoadingMore.set(false);
    this._matchesError.set(null);
    const t = this.usableToken(id, query.t) ?? undefined;
    this.api
      .getMatches(id, { ...query, t })
      .pipe(
        tap((matches) => {
          if (generation === this.matchesGeneration) this._matches.set(matches);
        }),
        catchError((err: unknown) => {
          const error = this.fail(err, BY_ID);
          if (generation === this.matchesGeneration) this._matchesError.set(error);
          return of(null);
        }),
        finalize(() => {
          if (generation === this.matchesGeneration) this._matchesLoading.set(false);
        }),
      )
      .subscribe();
  }

  /**
   * GET /:id/matches?page=N+1 with the relax level of the current result —
   * appends the next page (limit ≤ 40 per the contract). No-op unless the
   * loaded result belongs to `id` (offers of two requests are never mixed).
   */
  loadMatchesNextPage(id: string): void {
    if (this.focusId !== id) return;
    const current = this._matches();
    if (!current || !hasMoreMatches(current)) return;
    if (this._matchesLoading() || this._matchesLoadingMore()) return;
    const generation = ++this.matchesGeneration;
    const query: MatchesQuery = {
      relax: current.relaxLevel,
      page: (current.page ?? 1) + 1,
      limit: Math.min(40, current.limit || 12),
      t: this.storedToken(id) ?? undefined,
    };
    this._matchesLoadingMore.set(true);
    this._matchesError.set(null);
    this.api
      .getMatches(id, query)
      .pipe(
        tap((page) => {
          if (generation !== this.matchesGeneration) return;
          this._matches.update((prev) =>
            prev ? { ...page, items: appendUniqueById(prev.items, page.items) } : page,
          );
        }),
        catchError((err: unknown) => {
          const error = this.fail(err, BY_ID);
          if (generation === this.matchesGeneration) this._matchesError.set(error);
          return of(null);
        }),
        finalize(() => {
          if (generation === this.matchesGeneration) this._matchesLoadingMore.set(false);
        }),
      )
      .subscribe();
  }

  /** Matches known up-front (create response / navigation state) of the focused request. */
  setMatches(matches: MatchesResultDto | null): void {
    this.matchesGeneration++;
    this._matches.set(matches);
    this._matchesError.set(null);
    this._matchesLoading.set(false);
    this._matchesLoadingMore.set(false);
  }

  /** GET /:id/proposals — no-op for a request other than the focused one (`claimFocus`). */
  loadProposals(id: string, token?: string | null): void {
    if (!this.claimFocus(id)) return;
    this.proposalsRequested = true;
    this.fetchProposals(id, this.usableToken(id, token), false);
  }

  // ────────────────────────────────────────────────────────────
  // Mutations — return observables with mapped errors
  // ────────────────────────────────────────────────────────────

  /** POST / — on success stores the manage token and focuses the new request (current + matches). */
  create(dto: CreateRequestDto): Observable<CreateRequestResponseDto> {
    return this.runAction(this.api.create(dto)).pipe(
      tap((res) => {
        if (res.request?.id && res.manageToken) {
          this.tokens.set(res.request.id, res.manageToken, res.request.expiresAt);
        }
        if (res.request) this.showRequest(res.request);
        this._matches.set(res.matches);
        this._proposals.set([]);
        this.statsLoadedAt = null;
      }),
    );
  }

  /**
   * POST /:id/verify — `token` is the VERIFICATION token from the e-mail link
   * (never stored); the manage token comes back in the response and is
   * persisted for this browser (the confirmation page may be opened on a
   * device that never saw the form). Once verified (or definitively rejected)
   * the link's token is released from the URL holder — a reload of the
   * confirmation page no longer replays it; a transient failure keeps it there
   * for F5.
   */
  verify(id: string, token: string): Observable<RequestDetailDto> {
    return this.runAction(this.api.verify(id, token), BY_ID).pipe(
      tap({
        next: (res) => {
          if (res.manageToken) {
            this.tokens.set(id, res.manageToken, res.request?.expiresAt);
          }
          if (res.request) this.showRequest(res.request);
          this.statsLoadedAt = null;
          this.releaseLinkCredential(token);
        },
        error: (error: RequestBoardError) => this.releaseLinkCredential(token, error),
      }),
      map((res) => res.request),
    );
  }

  /** POST /:id/resend-verification — author only; `{ sent, reason? }`. */
  resendVerification(id: string, token?: string | null): Observable<ResendVerificationResponseDto> {
    const t = this.usableToken(id, token);
    if (!t) return this.missingToken(id);
    return this.runAction(this.api.resendVerification(id, t), BY_ID, { id, token: t });
  }

  update(id: string, dto: UpdateRequestDto, token?: string | null): Observable<RequestDetailDto> {
    const t = this.usableToken(id, token);
    if (!t) return this.missingToken(id);
    return this.runAction(this.api.update(id, t, dto), BY_ID, { id, token: t }).pipe(
      tap((request) => {
        if (!this.applyAuthorResult(id, t, request)) return;
        // Criteria changed — the shown matches are stale until they are reloaded.
        this.setMatches(null);
      }),
    );
  }

  /**
   * PATCH /:id { offersConsent } — withdraws (false) / re-grants (true) the
   * consent to owner proposals without closing the request. Matches are kept
   * (criteria did not change).
   */
  setOffersConsent(id: string, offersConsent: boolean, token?: string | null): Observable<RequestDetailDto> {
    const t = this.usableToken(id, token);
    if (!t) return this.missingToken(id);
    return this.runAction(this.api.update(id, t, { offersConsent }), BY_ID, { id, token: t }).pipe(
      tap((request) => this.applyAuthorResult(id, t, request)),
    );
  }

  /** POST /:id/close — the backend withdraws the open proposals, so they are re-read too. */
  close(id: string, reason: CloseReason, token?: string | null): Observable<RequestDetailDto> {
    const t = this.usableToken(id, token);
    if (!t) return this.missingToken(id);
    return this.runAction(this.api.close(id, t, reason), BY_ID, { id, token: t }).pipe(
      tap((request) => {
        if (this.applyAuthorResult(id, t, request) && this.proposalsRequested) {
          this.fetchProposals(id, t, true);
        }
        this.statsLoadedAt = null;
      }),
    );
  }

  extend(id: string, token?: string | null): Observable<RequestDetailDto> {
    const t = this.usableToken(id, token);
    if (!t) return this.missingToken(id);
    return this.runAction(this.api.extend(id, t), BY_ID, { id, token: t }).pipe(
      tap((request) => this.applyAuthorResult(id, t, request)),
    );
  }

  /** DELETE /:id — removes the stored token and clears current state. */
  remove(id: string, token?: string | null): Observable<void> {
    const t = this.usableToken(id, token);
    if (!t) return this.missingToken(id);
    return this.runAction(this.api.remove(id, t), BY_ID, { id, token: t }).pipe(
      tap(() => {
        this.tokens.remove(id);
        if (this.focusId === id || this._current()?.id === id) this.clearCurrent();
        this._board.update((prev) => prev.filter((r) => r.id !== id));
        this.statsLoadedAt = null;
      }),
    );
  }

  /** POST /:id/proposals/:pid/reply — marks the proposal as seen locally. */
  reply(id: string, proposalId: string, token?: string | null): Observable<ProposalReplyResponseDto> {
    const t = this.usableToken(id, token);
    if (!t) return this.missingToken(id);
    return this.runAction(this.api.replyToProposal(id, proposalId, t), BY_ID, { id, token: t }).pipe(
      tap((res) => {
        if (this.focusId !== id) return;
        this._proposals.update((prev) =>
          prev.map((p) =>
            p.id === proposalId
              ? { ...p, status: p.status === 'sent' ? 'seen' : p.status, threadId: res.threadId }
              : p,
          ),
        );
      }),
    );
  }

  /**
   * POST /:id/report — DSA notice. Independent of the author actions
   * (`submitting` is not touched); the error is mapped to `RequestBoardError`.
   */
  report(id: string, dto: ReportRequestDto): Observable<ReportRequestResponseDto> {
    return this.api
      .report(id, dto)
      .pipe(catchError((err: unknown) => throwError(() => this.fail(err, BY_ID))));
  }

  /** Clears the shared author-action error (e.g. when a dialog is (re)opened). */
  clearActionError(): void {
    this._actionError.set(null);
  }

  // ────────────────────────────────────────────────────────────
  // Helpers
  // ────────────────────────────────────────────────────────────

  /** Maps the error and reports a board switched off (503 DISABLED) to the config. */
  private fail(err: unknown, options?: MapRequestBoardErrorOptions): RequestBoardError {
    const error = mapRequestBoardError(err, options);
    if (isRequestBoardDisabledError(error)) this.configService.markDisabled();
    return error;
  }

  /** Moves the request scope to `id` — the state of another request is dropped. */
  private focus(id: string): void {
    if (this.focusId !== id) this.resetRequestScope(id);
  }

  /**
   * Scope check of the `load*` calls: nothing focused → focus `id`; `id`
   * focused → go on; another request focused → false (no-op). The detail page
   * calls `load*` with an id captured BEFORE an A → B navigation (`loadMatches`
   * after a PATCH of A, `loadRequest` after a resend of A): such a late call
   * must neither move the focus back to A nor wipe B. A real switch goes
   * through `clearCurrent()` (the page does it on every id change).
   */
  private claimFocus(id: string): boolean {
    if (this.focusId === null) this.resetRequestScope(id);
    return this.focusId === id;
  }

  /**
   * A credential (manage token from `?t=`, verification token) the backend
   * gave its final answer for — accepted, or rejected with a 4xx (`error`) —
   * is dropped from the URL holder, whose per-tab copy would otherwise replay
   * it on a reload. Transient failures keep it there (F5 retries with it).
   */
  private releaseLinkCredential(token: string | null, error?: RequestBoardError): void {
    if (!token || (error && !isDefinitiveRejection(error))) return;
    forgetSensitiveValue(token);
  }

  private resetRequestScope(focusId: string | null): void {
    this.focusId = focusId;
    // Responses still in flight belong to the old scope.
    this.currentGeneration++;
    this.matchesGeneration++;
    this.proposalsGeneration++;
    this.proposalsRequested = false;
    this._current.set(null);
    this._currentLoading.set(false);
    this._currentError.set(null);
    this._matches.set(null);
    this._matchesLoading.set(false);
    this._matchesLoadingMore.set(false);
    this._matchesError.set(null);
    this._proposals.set([]);
    this._proposalsLoading.set(false);
    this._proposalsError.set(null);
    this._actionError.set(null);
  }

  /** Shows a detail obtained outside `loadRequest` (create / verify / setCurrent). */
  private showRequest(request: RequestDetailDto): void {
    this.focus(request.id);
    this.currentGeneration++;
    this._current.set(request);
    this._currentError.set(null);
    this._currentLoading.set(false);
  }

  /**
   * GET /:id. `silent` = background refresh after an action error: no spinner,
   * the shown data stays on transient errors (only „not found” replaces it).
   * The backend's answer settles a token taken from a link (`releaseLinkCredential`).
   */
  private fetchCurrent(id: string, token: string | null, silent: boolean): void {
    const generation = ++this.currentGeneration;
    if (!silent) {
      this._currentLoading.set(true);
      this._currentError.set(null);
    }
    this.api
      .getDetail(id, token)
      .pipe(
        tap((request) => {
          if (generation !== this.currentGeneration) return;
          this.syncTokenWithDetail(id, token, request);
          // Author view (token persisted above) or not the author's: settled either way.
          this.releaseLinkCredential(token);
          this._current.set(request);
          this._currentError.set(null);
        }),
        catchError((err: unknown) => {
          const error = this.fail(err, BY_ID);
          this.releaseLinkCredential(token, error);
          if (generation === this.currentGeneration && (!silent || error.errorCode === NOT_FOUND)) {
            this._currentError.set(error);
            this._current.set(null);
          }
          return of(null);
        }),
        finalize(() => {
          if (generation === this.currentGeneration) this._currentLoading.set(false);
        }),
      )
      .subscribe();
  }

  /** GET /:id/proposals (`silent` — see `fetchCurrent`). */
  private fetchProposals(id: string, token: string | null, silent: boolean): void {
    const generation = ++this.proposalsGeneration;
    if (!silent) {
      this._proposalsLoading.set(true);
      this._proposalsError.set(null);
    }
    this.api
      .getProposals(id, token)
      .pipe(
        tap((proposals) => {
          if (generation !== this.proposalsGeneration) return;
          this._proposals.set(proposals);
          this._proposalsError.set(null);
        }),
        catchError((err: unknown) => {
          const error = this.fail(err, BY_ID);
          if (generation === this.proposalsGeneration && !silent) {
            this._proposalsError.set(error);
            this._proposals.set([]);
          }
          return of(null);
        }),
        finalize(() => {
          if (generation === this.proposalsGeneration) this._proposalsLoading.set(false);
        }),
      )
      .subscribe();
  }

  /**
   * GET made with `token`: the author's view refreshes the local token validity
   * (`expiresAt` may have moved); a non-author view means the backend no longer
   * accepts it — it is forgotten (only if it is still the stored one).
   */
  private syncTokenWithDetail(id: string, token: string | null, request: RequestDetailDto): void {
    if (!token) return;
    if (request.isAuthor) this.tokens.set(id, token, request.expiresAt);
    else if (this.storedToken(id) === token) this.tokens.remove(id);
  }

  /**
   * An author mutation answered with the fresh detail: refresh the token
   * validity (extend / date edits move `expiresAt`) and show the detail when
   * `id` is the focused request. Returns whether the state was updated.
   */
  private applyAuthorResult(id: string, token: string, request: RequestDetailDto): boolean {
    if (request.isAuthor) this.tokens.set(id, token, request.expiresAt);
    if (this.focusId !== id) return false;
    // An older GET still in flight must not overwrite this fresher result.
    this.currentGeneration++;
    this._current.set(request);
    this._currentError.set(null);
    this._currentLoading.set(false);
    return true;
  }

  /** Re-reads the focused request (and its proposals) in the background. */
  private refreshFocused(id: string): void {
    if (this.focusId !== id) return;
    const token = this.storedToken(id);
    this.fetchCurrent(id, token, true);
    if (this.proposalsRequested) this.fetchProposals(id, token, true);
  }

  /**
   * Wraps a mutation: `submitting` / `actionError` are only touched once the
   * returned (cold) observable is actually subscribed, and released in
   * `finalize` — an unsubscribed or torn-down stream cannot leave the flag stuck.
   * Author actions (`author`): 403 INVALID_TOKEN forgets the token, and after
   * INVALID_STATE / NOT_FOUND / INVALID_TOKEN / PROPOSAL_NOT_FOUND the focused
   * request is re-read (its status / proposals changed in parallel).
   */
  private runAction<T>(
    source: Observable<T>,
    options?: MapRequestBoardErrorOptions,
    author?: AuthorCall,
  ): Observable<T> {
    return defer(() => {
      this._pendingActions.update((n) => n + 1);
      this._actionError.set(null);
      return source;
    }).pipe(
      catchError((err: unknown) => {
        const error = this.fail(err, options);
        if (!author || this.isFocusable(author.id)) this._actionError.set(error);
        if (author) this.afterAuthorActionError(author, error);
        return throwError(() => error);
      }),
      finalize(() => this._pendingActions.update((n) => Math.max(0, n - 1))),
    );
  }

  private afterAuthorActionError(author: AuthorCall, error: RequestBoardError): void {
    if (error.errorCode === INVALID_TOKEN && this.storedToken(author.id) === author.token) {
      this.tokens.remove(author.id);
    }
    if (STALE_AFTER_ACTION_CODES.has(error.errorCode)) this.refreshFocused(author.id);
  }

  /** The action error of `id` may be shown: it is the focused request (or nothing is focused). */
  private isFocusable(id: string): boolean {
    return this.focusId === null || this.focusId === id;
  }

  /**
   * The given token, or the stored one when none is given or the given one
   * cannot be a header value (`isHeaderSafeToken` — e.g. `?t=` of a crafted
   * or mangled link): such a value would only be dropped by the API service,
   * while the token kept for this request still works.
   */
  private usableToken(id: string, token?: string | null): string | null {
    return isHeaderSafeToken(token) ? token : this.storedToken(id);
  }

  /**
   * The stored manage token, read WITHOUT tracking: the imperative methods are
   * also called from effects (e.g. `loadProposals`), which must not re-run on
   * every change of the token map.
   */
  private storedToken(id: string): string | null {
    return untracked(() => this.tokens.get(id));
  }

  /**
   * No token for an author action (e.g. it expired locally or another tab
   * removed it): fail with INVALID_TOKEN and re-read the request — the page
   * still shows the author view.
   */
  private missingToken<T>(id: string): Observable<T> {
    return defer(() => {
      const error: RequestBoardError = {
        status: 0,
        errorCode: INVALID_TOKEN,
        message: 'Missing manage token',
      };
      if (this.isFocusable(id)) this._actionError.set(error);
      this.refreshFocused(id);
      return throwError(() => error);
    });
  }
}
